import { NavState } from '@amzn/aac-chrome-internal-api';

/**
 * Shadow-mode plumbing for the MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM weblab (T2
 * treatment). Callers gate on `useIsFeatureEnabled(...MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM_T2)`
 * before invoking these helpers; the helpers themselves do not evaluate the
 * treatment. FE always renders from Trim under T1 AND T2; T2 additionally
 * fires a shadow BTAS navState call, diffs it against Trim, and emits
 * Shadow.* metrics.
 */

let pageShadowId: string | null = null;
let btasNavStatePromise: Promise<NavState> | null = null;

const uuidV4Regex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Mints (or returns the already-minted) shadowId for this page load. One
 * shadowId is shared across the BTAS shadow call, navbar-init, and
 * accountswitcher-init so an offline joiner can stitch the three log lines
 * (future slice 20.4). Not sent as a header today — the AACChromeInternalAPI
 * client does not accept per-call headers yet.
 */
export const getOrMintShadowId = (): string => {
  if (pageShadowId) return pageShadowId;
  // crypto.randomUUID is Node 18+ / all modern browsers. Fall back to a
  // deterministic timestamp+random string only if crypto is unavailable so a
  // test environment or a downlevel browser never throws through shadow code.
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      pageShadowId = crypto.randomUUID();
      return pageShadowId;
    }
  } catch {
    // fall through
  }
  pageShadowId = `${Date.now().toString(16)}-${Math.random()
    .toString(16)
    .slice(2, 10)}`;
  return pageShadowId;
};

export const getShadowId = (): string | null => pageShadowId;

/**
 * Returns the `X-Aac-Shadow-Id` header object. Callers must gate on the T2
 * feature flag before invoking; on non-T2 treatments the request should go
 * out header-free so the backend does not log a shadow line for it.
 *
 * SDK header names are case-insensitive; the exact spelling here matches
 * what BTAS (slice 20.1) and TrimService (slice 20.3) read.
 */
export const SHADOW_ID_HEADER_NAME = 'X-Aac-Shadow-Id';
export const getShadowHeaders = (): Record<string, string> => {
  return { [SHADOW_ID_HEADER_NAME]: getOrMintShadowId() };
};

/**
 * Stashes the BTAS navState promise so useAccountData (fired lazily on
 * dropdown-open) can await it later for the accountInfo diff. Only Nav.tsx
 * calls this; setting it a second time is a no-op so downstream diffs share
 * one source of truth. Callers pass the raw promise (may resolve or reject).
 */
export const setBtasNavStatePromise = (promise: Promise<NavState>): void => {
  if (btasNavStatePromise) return;
  btasNavStatePromise = promise;
};

export const getBtasNavStatePromise = (): Promise<NavState> | null =>
  btasNavStatePromise;

/**
 * Test-only reset. Not exported through the barrel — reserved for the diff
 * site tests that need a clean module state between cases.
 */
export const resetShadowContextForTest = (): void => {
  pageShadowId = null;
  btasNavStatePromise = null;
};

export const isValidUuidV4ForTest = (value: string): boolean =>
  uuidV4Regex.test(value);
